// config/constants.ts

// Polling & Debounce
export const POLLING_DURATION = 10_000;
export const DEBOUNCE_TIME = 1000;
export const SEARCH_DEBOUNCE = 300;

// Table dimensions
export const TABLE_HEIGHT = '1200px';
export const DEFAULT_COLUMN_WIDTH = 100;
export const MIN_SPARE_ROWS = 3;

// Colors
export const COLORS = {
  // Tracking status
  trackingHighlight: '#fef3c7',
  trackingBorder: '#f59e0b',

  // Delays
  minorDelayBg: '#fef3c7',
  estimatedMinorDelayBg: '#fffbeb',
  minorDelayBorder: '#f59e0b',
  estimatedMinorDelayBorder: '#fbbf24',
  majorDelayBg: '#fee2e2',
  estimatedMajorDelayBg: '#fef2f2',
  majorDelayBorder: '#ef4444',
  estimatedMajorDelayBorder: '#f87171',

  // Success
  onTimeBg: '#dcfce7',
  onTimeBorder: '#22c55e',
  onTimeText: '#16a34a',

  // Estimated on-time (lighter variants)
  estimatedOnTimeBg: '#f0fdf4',
  estimatedOnTimeBorder: '#86efac',
  // Estimated date delays (lighter variants)

  // Gray out (no events)
  noEventsBg: '#f3f4f6',
  noEventsText: '#6b7280',

  // Search highlight
  searchResult: '#ffeb3b',
  searchBorder: '#ff9800',
  currentResult: '#fffde7',
  currentBorder: '#f9a825',

  // Headers
  headerBg: '#c5cef2',
  headerHighlight: '#1a42e8',

  // Tracking job errors
  errorIcon: '#ef4444',
  warningIcon: '#f59e0b',
} as const;

// Dropdown options
export const STATUS_OPTIONS = [
  'Ordered',
  'Booked',
  'Loading',
  'Departed',
  'Transshipment',
  'Pre-Arrival',
  'In Port',
  'Delivered',
] as const;

export const CARRIER_OPTIONS = [
  'Maersk',
  'Hapag-Lloyd',
  'CMA CGM',
  'OOCL',
  'HMM',
  'ONE',
  'Evergreen',
  'MSC',
  'Yang Ming',
  'ZIM',
  'COSCO',
] as const;

// Thresholds
export const MAJOR_DELAY_THRESHOLD_DAYS = 5;

// Default columns as object with key as the key
export const DEFAULT_COLUMNS_MAP = {
  internalReference: {
    key: 'internalReference',
    label: 'Internal Reference',
    dataType: 'text',
    width: DEFAULT_COLUMN_WIDTH * 0.8,
  },
  client: {
    key: 'client',
    label: 'Client',
    dataType: 'text',
    width: DEFAULT_COLUMN_WIDTH,
  },
  status: {
    key: 'status',
    label: 'Status',
    dataType: 'text',
    width: DEFAULT_COLUMN_WIDTH * 0.6,
    type: 'dropdown',
    source: STATUS_OPTIONS as any,
  },
  bookingNumber: {
    key: 'bookingNumber',
    label: 'Booking Number',
    dataType: 'text',
    width: DEFAULT_COLUMN_WIDTH * 0.6,
  },
  container: {
    key: 'container',
    label: 'Container',
    dataType: 'text',
    width: DEFAULT_COLUMN_WIDTH * 0.8,
  },
  carrier: {
    key: 'carrier',
    label: 'Carrier',
    dataType: 'text',
    width: DEFAULT_COLUMN_WIDTH * 0.6,
    type: 'dropdown',
    source: CARRIER_OPTIONS,
  },
  etd: {
    key: 'etd',
    label: 'ETD',
    dataType: 'date',
    dateFormat: 'DD/MM/YYYY',
    width: DEFAULT_COLUMN_WIDTH * 1.2,
    correctFormat: true,
  },
  atd: {
    key: 'atd',
    label: 'ATD',
    dataType: 'date',
    dateFormat: 'DD/MM/YYYY',
    width: DEFAULT_COLUMN_WIDTH,
    correctFormat: true,
  },
  eta: {
    key: 'eta',
    label: 'ETA',
    dataType: 'date',
    dateFormat: 'DD/MM/YYYY',
    width: DEFAULT_COLUMN_WIDTH * 1.2,
    correctFormat: true,
  },
  ata: {
    key: 'ata',
    label: 'ATA',
    dataType: 'date',
    dateFormat: 'DD/MM/YYYY',
    width: DEFAULT_COLUMN_WIDTH,
    correctFormat: true,
  },
  originPort: {
    key: 'originPort',
    label: 'Origin Port',
    dataType: 'text',
    width: DEFAULT_COLUMN_WIDTH * 0.8,
  },
  destinationPort: {
    key: 'destinationPort',
    label: 'Destination Port',
    dataType: 'text',
    width: DEFAULT_COLUMN_WIDTH * 0.8,
  },
};

// Column sort order
export const COLUMN_SORT_ORDER = [
  'id',
  'internalReference',
  'client',
  'status',
  'bookingNumber',
  'container',
  'carrier',
  'etd',
  'eta',
  'originPort',
  'destinationPort',
] as const;

export const TRACKING_DATE_FIELDS = ['eta', 'etd'];
